import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { Estimate } from '@interfaces/Estimate';
import { EstimateRevisionDto } from '@interfaces/EstimateRevisionDto';
import { TranslateService } from '@ngx-translate/core';
import { CurrencySymbolService } from '@shared/services/currency-symbol.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { EMPTY } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'pop-expansion-panel',
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.less']
})
export class ExpansionPanelComponent extends BaseComponent implements OnInit, OnDestroy {
  public currentEstimate: Estimate;
  public currentLang: string;
  public currencySymbol: string;
  public estimatedFees: number;
  public isHidden = false;
  public estimateRevisionVMs: EstimateRevisionDto[];

  @Input() public isGroupApproved: boolean;
  @Input() public hasCRM: boolean;

  private _panelOpenState = false;
  @Input() public get panelOpenState() {
    return this._panelOpenState;
  }
  public set panelOpenState(value: boolean) {
    this._panelOpenState = value;
    this.panelOpenStateChange.emit(value);
  }

  @Output() public panelOpenStateChange = new EventEmitter<boolean>();

  public constructor(
    private estimateService: EstimateService,
    private router: Router,
    private translate: TranslateService,
    private currencySymbolService: CurrencySymbolService,
    private loadingService: LoadingService
  ) {
    super();
    this.currentEstimate = <Estimate>{};
    this.estimatedFees = 0;
    this.initialiseEstimate();
    this.estimateRevisionVMs = <EstimateRevisionDto[]>[];
    this.currentLang = this.translate.currentLang;
  }

  private initialiseEstimate() {
    this.subscribe(
      this.estimateService
        .observeCurrentEstimate()
        .pipe(
          switchMap(data => {
            this.currentEstimate = data ?? this.estimateService.currentEstimate;
            return data ? this.estimateService.getEstimateRevisionDtos(this.currentEstimate.estimateGroupId) : EMPTY;
          })
        )
        .pipe(
          switchMap(data => {
            this.estimateRevisionVMs = data;

            if (this.hasCRM) {
              return this.estimateService.getEstimatedFeesCRM(this.currentEstimate.codeRef);
            } else {
              return EMPTY;
            }
          })
        )
        .subscribe(result => {
          this.estimatedFees = result;
        })
    );
  }

  public ngOnInit() {
    this.subscribe(
      this.currencySymbolService
        .getCurrentCurrencySymbol()
        .subscribe(currencySymbol => (this.currencySymbol = currencySymbol))
    );
  }

  public togglePanel() {
    this.panelOpenState = !this.panelOpenState;
    this.isHidden = !this.isHidden;
  }

  private getMaxRevisionValue(): number {
    let maxRevision = 0;
    const revisions: number[] = <number[]>[];

    for (const estimateRevisionVM of this.estimateRevisionVMs) {
      revisions.push(estimateRevisionVM.revision);
    }

    maxRevision = Math.max(...revisions);

    return maxRevision;
  }

  public async addNewRevision() {
    await this.loadingService.run(async () => {
      const newRevision: Estimate = this.estimateService.addRevision(this.currentEstimate, this.getMaxRevisionValue());
      await this.estimateService.saveChangesAsync();
      await this.router.navigate(['/app/estimate-detail', newRevision.id]);
    });
  }
}
